import React, { FC, useMemo } from "react";
import { Modal, Form, Button, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "~/components/date-picker";

const { Text } = Typography;

type FormValues = {
  dateRange?: [Dayjs, Dayjs];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  mode: "phase" | "task";
  title: string;
  startDate?: Date | null;
  deadlineDate?: Date | null;
  onSave: (values: { startDate?: Date | null; deadlineDate?: Date | null }) => void;
};

export type TimelineEditContext = {
  type: "phase" | "task";
  id: number;
  startDate?: Date | null;
  deadlineDate?: Date | null;
  phaseId: number;
  title?: string;
  html?: string | null;
  description?: string;
};

export const TimelineModal: FC<Props> = ({
  isOpen,
  onClose,
  mode,
  title,
  startDate,
  deadlineDate,
  onSave,
}) => {
  const [form] = Form.useForm<FormValues>();

  const duration = useMemo(() => {
    const range = form.getFieldValue("dateRange");
    if (range?.[0] && range?.[1]) {
      return range[1].diff(range[0], "day") + 1;
    }
    return null;
  }, [form.getFieldValue("dateRange")]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave({
        startDate: values.dateRange?.[0]?.toDate() ?? null,
        deadlineDate: values.dateRange?.[1]?.toDate() ?? null,
      });
      onClose();
    } catch (error) {}
  };

  const handleClear = () => {
    onSave({ startDate: null, deadlineDate: null });
    onClose();
  };

  return (
    <Modal
      title={`Edit Timeline for ${mode === "phase" ? "Phase" : "Task"} - ${title}`}
      open={isOpen}
      footer={[
        <Button key="clear" danger icon={<DeleteOutlined />} onClick={handleClear}>
          Clear Dates
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk}>
          Save
        </Button>,
      ]}
      onCancel={onClose}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          dateRange:
            startDate && deadlineDate ? [dayjs(startDate), dayjs(deadlineDate)] : undefined,
        }}
      >
        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[
            { required: true, message: "Date range is required" },
            ({ getFieldValue }) => ({
              validator(_, value: [Dayjs, Dayjs] | undefined) {
                if (!value) return Promise.resolve();
                if (value[0] && !value[1]) {
                  return Promise.reject(new Error("Please select an end date"));
                }
                if (!value[0] && value[1]) {
                  return Promise.reject(new Error("Please select a start date"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <DatePicker.RangePicker style={{ width: "100%" }} />
        </Form.Item>
        {duration && (
          <div>
            <Text type="secondary">
              Duration: {duration} day{duration === 1 ? "" : "s"}
            </Text>
          </div>
        )}
      </Form>
    </Modal>
  );
};
